/** Unified member directory — one card per person, no duplication.
 *
 * The old members page listed every member once (MemberList), then listed
 * the operators AGAIN with their schedule (OperatorsSection), and a third
 * time inside the availability board — so an operator showed up three
 * times with the same name and email. Here each member appears exactly
 * once: role tags always, plus — only when the member is an operator —
 * live availability, campaign load, schedule, and the Manage button.
 * Manage is operator-only because /admin/operators/<id> exists for the
 * `inspector` role alone; for anyone else it would 404.
 *
 * Consumes GET /v1/orgs/members (the roster), /v1/operators/availability
 * (live presence, polled) and /v1/operators (campaign load + schedule).
 * The latter two are permission-gated server-side, so a member who can
 * read the roster but not the operator detail still gets a usable page —
 * the operator extras just stay empty.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { PermissionGate } from "@/components/PermissionGate";
import { useHasPermission } from "@/lib/permissions";
import { ApiPaths, api } from "@/lib/api";
import { describeApiError } from "@/lib/apiErrors";
import { formatDate } from "@/lib/format";
import { roleLabel, roleLabels } from "@/lib/roleLabels";
import { MemberDsrActions, fetchOrgMembers, type OrgMember } from "@/modules/dsr";

/** The role slug that makes someone an operator (runs inspections). */
const OPERATOR_SLUG = "inspector";

type PresenceStatus = "in_call" | "available" | "off_shift" | "no_schedule";

type OperatorPresence = {
  user_id: number;
  status: PresenceStatus;
  in_schedule: boolean | null;
};

type PresenceResponse = {
  operators: OperatorPresence[];
  summary: Record<PresenceStatus, number>;
};

type OperatorRow = {
  user_id: number;
  campaign_count: number;
  schedule: {
    has_schedule: boolean;
    weekly_hours: number;
    days_worked: number;
    timezone: string;
    upcoming_time_off: number;
  };
};

const STATUS_DOT: Record<PresenceStatus, string> = {
  in_call: "bg-nb-mint",
  available: "bg-emerald-500",
  off_shift: "bg-nb-text-3",
  no_schedule: "bg-amber-500",
};

const STATUS_TONE: Record<PresenceStatus, string> = {
  in_call: "border-nb-mint/40 bg-nb-mint/10 text-nb-mint",
  available: "border-emerald-500/40 bg-emerald-500/10 text-emerald-400",
  off_shift: "border-nb-border bg-nb-surface/60 text-nb-text-3",
  no_schedule: "border-amber-500/40 bg-amber-500/10 text-amber-400",
};

const STATUS_KEY: Record<PresenceStatus, string> = {
  in_call: "admin.operators.presence.inCall",
  available: "admin.operators.presence.available",
  off_shift: "admin.operators.presence.offShift",
  no_schedule: "admin.operators.presence.noSchedule",
};

const STATUS_ORDER: PresenceStatus[] = [
  "in_call",
  "available",
  "off_shift",
  "no_schedule",
];

/** Role slugs first, in the order an admin thinks about them; the rest
 *  (custom roles) keep alphabetical order after these. */
const ROLE_ORDER = ["owner", "org_admin", OPERATOR_SLUG, "observer", "auditor", "member"];

function displayName(m: OrgMember): string {
  return `${m.first_name} ${m.last_name}`.trim() || m.email;
}

function initials(m: OrgMember): string {
  const first = m.first_name.trim();
  const last = m.last_name.trim();
  if (first || last) return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
  return m.email.charAt(0).toUpperCase();
}

function isOperator(m: OrgMember): boolean {
  return m.role_slugs.includes(OPERATOR_SLUG);
}

function useInvalidateMembers() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ["org-members"] });
}

function RemoveMemberButton({ member }: { member: OrgMember }) {
  const { t } = useTranslation();
  const invalidate = useInvalidateMembers();
  const remove = useMutation({
    mutationFn: () => api.delete(ApiPaths.orgMember(member.user_id)),
    onSuccess: invalidate,
  });

  return (
    <PermissionGate perm="org.member.remove">
      <button
        type="button"
        className="btn-danger text-xs"
        disabled={remove.isPending}
        title={
          remove.isError
            ? describeApiError(remove.error, t("admin.members.removeFailed"))
            : undefined
        }
        onClick={() => {
          if (
            window.confirm(
              t("admin.members.removeConfirm", { email: member.email }),
            )
          ) {
            remove.mutate();
          }
        }}
      >
        {remove.isPending
          ? t("admin.members.removing")
          : t("admin.members.remove")}
      </button>
    </PermissionGate>
  );
}

function ReactivateMemberButton({ member }: { member: OrgMember }) {
  const { t } = useTranslation();
  const invalidate = useInvalidateMembers();
  const reactivate = useMutation({
    mutationFn: () => api.post(ApiPaths.orgMemberReactivate(member.user_id)),
    onSuccess: invalidate,
  });

  return (
    <button
      type="button"
      className="btn-ghost text-xs"
      disabled={reactivate.isPending}
      title={
        reactivate.isError
          ? describeApiError(reactivate.error, t("admin.members.reactivateFailed"))
          : undefined
      }
      onClick={() => reactivate.mutate()}
    >
      {reactivate.isPending
        ? t("admin.members.reactivating")
        : t("admin.members.reactivate")}
    </button>
  );
}

/** Collapsible list of deactivated members with a per-row Reactivate. */
function DeactivatedMembers() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const inactive = useQuery({
    queryKey: ["org-members", "inactive"],
    queryFn: () => fetchOrgMembers("inactive"),
    enabled: open,
  });

  return (
    <PermissionGate perm="org.member.remove">
      <div className="mt-5">
        <button
          type="button"
          className="text-xs text-nb-text-2 underline hover:text-nb-text"
          onClick={() => setOpen((v) => !v)}
        >
          {open
            ? t("admin.members.hideDeactivated")
            : t("admin.members.showDeactivated")}
        </button>
        {open && (
          <div className="mt-2">
            {inactive.isLoading && (
              <p className="text-xs text-nb-text-3">
                {t("admin.members.loadingMembers")}
              </p>
            )}
            {inactive.data && inactive.data.length === 0 && (
              <p className="text-xs text-nb-text-3">
                {t("admin.members.noDeactivated")}
              </p>
            )}
            {inactive.data && inactive.data.length > 0 && (
              <ul className="divide-y divide-nb-border rounded-xl border border-nb-border bg-nb-surface/20">
                {inactive.data.map((m) => (
                  <li
                    key={m.user_id}
                    className="flex items-center justify-between gap-3 px-4 py-2.5"
                  >
                    <div className="min-w-0 flex-1">
                      <div
                        className="truncate text-sm text-nb-text-2"
                        title={m.email}
                      >
                        {m.email}
                      </div>
                      <div className="text-xs text-nb-text-3">
                        {m.role_slugs.length > 0
                          ? roleLabels(t, m.role_slugs)
                          : t("admin.members.noRoles")}
                      </div>
                    </div>
                    {!m.erased && <ReactivateMemberButton member={m} />}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </PermissionGate>
  );
}

function AvailabilityPill({
  status,
  inSchedule,
}: {
  status: PresenceStatus;
  inSchedule: boolean | null;
}) {
  const { t } = useTranslation();
  return (
    <span
      data-testid={`availability-${status}`}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-medium ${STATUS_TONE[status]}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${STATUS_DOT[status]}`} />
      {t(STATUS_KEY[status])}
      {status === "in_call" && inSchedule === false && (
        <span className="text-amber-400">
          · {t("admin.operators.presence.offShiftFlag")}
        </span>
      )}
    </span>
  );
}

function MemberCard({
  member,
  presence,
  operator,
}: {
  member: OrgMember;
  presence?: OperatorPresence;
  operator?: OperatorRow;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const operatorRole = isOperator(member);
  const schedule = operator?.schedule;
  // An operator the availability feed hasn't heard of yet still deserves a
  // truthful pill: no schedule on file is exactly the "no_schedule" state.
  const status: PresenceStatus | null =
    presence?.status ??
    (operatorRole && schedule && !schedule.has_schedule ? "no_schedule" : null);

  return (
    <article className="card flex flex-col gap-3" data-testid="member-card">
      <div className="flex items-start gap-3">
        <span
          aria-hidden="true"
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-nb-surface text-sm font-semibold text-nb-text-2 ring-1 ring-nb-border"
        >
          {initials(member)}
        </span>
        <div className="min-w-0 flex-1">
          <div
            className={`truncate font-medium ${member.erased ? "italic text-nb-text-3" : "text-nb-text"}`}
            title={displayName(member)}
          >
            {displayName(member)}
          </div>
          <div className="truncate text-xs text-nb-text-3" title={member.email}>
            {member.email}
          </div>
        </div>
        {status && <AvailabilityPill status={status} inSchedule={presence?.in_schedule ?? null} />}
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        {member.role_slugs.length === 0 && (
          <span className="rounded-full border border-nb-border px-2 py-0.5 text-[11px] text-nb-text-3">
            {t("admin.members.noRoles")}
          </span>
        )}
        {member.role_slugs.map((slug) => (
          <span
            key={slug}
            className={`rounded-full border px-2 py-0.5 text-[11px] font-medium ${
              slug === OPERATOR_SLUG
                ? "border-sky-500/40 bg-sky-500/10 text-sky-400"
                : "border-nb-border bg-nb-surface/60 text-nb-text-2"
            }`}
          >
            {roleLabel(t, slug)}
          </span>
        ))}
      </div>

      {operatorRole && schedule && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-nb-text-2">
          <span className="text-nb-text">
            {t("admin.operators.campaignsCount", {
              count: operator?.campaign_count ?? 0,
            })}
          </span>
          <span className="text-nb-text-3">
            {schedule.has_schedule
              ? t("admin.operators.scheduleSummary", {
                  hours: schedule.weekly_hours,
                  days: schedule.days_worked,
                  tz: schedule.timezone,
                })
              : t("admin.operators.noSchedule")}
          </span>
          {schedule.upcoming_time_off > 0 && (
            <span className="rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] text-amber-300">
              {t("admin.operators.timeOff", {
                count: schedule.upcoming_time_off,
              })}
            </span>
          )}
        </div>
      )}

      <div className="mt-auto flex flex-wrap items-center justify-between gap-2 border-t border-nb-border/60 pt-3">
        <span className="text-[11px] text-nb-text-3">
          {member.last_active_at
            ? t("admin.members.lastActiveAt", {
                date: formatDate(member.last_active_at),
              })
            : t("admin.members.neverActive")}
        </span>
        <div className="flex items-center gap-2">
          <MemberDsrActions member={member} />
          {!member.erased && <RemoveMemberButton member={member} />}
          {/* Manage opens the operator editor (schedule, campaigns, time
              off) — a route that only exists for operators. */}
          {operatorRole && (
            <PermissionGate perm="org.member.read">
              <button
                type="button"
                className="btn-ghost text-xs"
                onClick={() => navigate(`/admin/operators/${member.user_id}`)}
              >
                {t("admin.operators.manage")}
              </button>
            </PermissionGate>
          )}
        </div>
      </div>
    </article>
  );
}

export function MemberDirectory() {
  const { t } = useTranslation();
  const canReadOperators = useHasPermission("org.member.read");
  const [roleFilter, setRoleFilter] = useState<string>("all");
  const [search, setSearch] = useState("");

  const members = useQuery({
    queryKey: ["org-members"],
    queryFn: () => fetchOrgMembers(),
  });

  const availability = useQuery({
    queryKey: ["operator-availability"],
    queryFn: async () =>
      (await api.get<PresenceResponse>(ApiPaths.operatorAvailability)).data,
    refetchInterval: 20000,
    enabled: canReadOperators,
  });

  const operators = useQuery({
    queryKey: ["operators"],
    queryFn: async () => (await api.get<OperatorRow[]>(ApiPaths.operators)).data,
    enabled: canReadOperators,
  });

  const presenceBy = useMemo(() => {
    const map = new Map<number, OperatorPresence>();
    for (const o of availability.data?.operators ?? []) map.set(o.user_id, o);
    return map;
  }, [availability.data]);

  const operatorBy = useMemo(() => {
    const map = new Map<number, OperatorRow>();
    for (const o of operators.data ?? []) map.set(o.user_id, o);
    return map;
  }, [operators.data]);

  // useMemo'd so the `?? []` fallback isn't a fresh array each render —
  // it feeds the two memos below.
  const all = useMemo(() => members.data ?? [], [members.data]);

  /** Filter chips are built from the roles the org actually uses, so an
   *  org with no auditors never sees a dead "Auditor (0)" chip. */
  const roleChips = useMemo(() => {
    const counts = new Map<string, number>();
    for (const m of all) {
      for (const slug of m.role_slugs) {
        counts.set(slug, (counts.get(slug) ?? 0) + 1);
      }
    }
    return [...counts.entries()].sort((a, b) => {
      const ia = ROLE_ORDER.indexOf(a[0]);
      const ib = ROLE_ORDER.indexOf(b[0]);
      if (ia !== -1 || ib !== -1) {
        return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
      }
      return a[0].localeCompare(b[0]);
    });
  }, [all]);

  const shown = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return all.filter((m) => {
      if (roleFilter !== "all" && !m.role_slugs.includes(roleFilter)) return false;
      if (!needle) return true;
      return (
        m.email.toLowerCase().includes(needle) ||
        displayName(m).toLowerCase().includes(needle)
      );
    });
  }, [all, roleFilter, search]);

  const summary = availability.data?.summary;
  const hasOperators = all.some(isOperator);

  return (
    <section>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">{t("admin.members.sectionTitle")}</h2>
          <p className="text-xs text-nb-text-3">
            {t("admin.members.showingCount", {
              shown: shown.length,
              total: all.length,
            })}
          </p>
        </div>
        <input
          type="search"
          className="input h-9 w-full max-w-xs text-sm"
          placeholder={t("admin.members.searchPlaceholder")}
          aria-label={t("admin.members.searchPlaceholder")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* Availability totals live here rather than in a separate board —
          the per-operator states are already on the cards below. */}
      {hasOperators && summary && (
        <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1 rounded-xl border border-nb-border bg-nb-surface/30 px-3 py-2 text-xs text-nb-text-2">
          <span className="font-medium text-nb-text">
            {t("admin.operators.presence.title")}
          </span>
          {STATUS_ORDER.map((s) => (
            <span key={s} className="inline-flex items-center gap-1.5">
              <span className={`h-2 w-2 rounded-full ${STATUS_DOT[s]}`} />
              {t(STATUS_KEY[s])}: {summary[s]}
            </span>
          ))}
        </div>
      )}

      {all.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <button
            type="button"
            aria-pressed={roleFilter === "all"}
            className={`rounded-full border px-3 py-1 text-xs font-medium transition ${
              roleFilter === "all"
                ? "border-nb-accent bg-nb-accent/10 text-nb-accent"
                : "border-nb-border text-nb-text-2 hover:text-nb-text"
            }`}
            onClick={() => setRoleFilter("all")}
          >
            {t("admin.members.filterAll")} ({all.length})
          </button>
          {roleChips.map(([slug, count]) => (
            <button
              key={slug}
              type="button"
              aria-pressed={roleFilter === slug}
              className={`rounded-full border px-3 py-1 text-xs font-medium transition ${
                roleFilter === slug
                  ? "border-nb-accent bg-nb-accent/10 text-nb-accent"
                  : "border-nb-border text-nb-text-2 hover:text-nb-text"
              }`}
              onClick={() => setRoleFilter(slug)}
            >
              {roleLabel(t, slug)} ({count})
            </button>
          ))}
        </div>
      )}

      {members.isLoading && (
        <p className="text-sm text-nb-text-2">{t("admin.members.loadingMembers")}</p>
      )}
      {members.isError && (
        <p className="card text-sm text-cvp-danger">
          {t("admin.members.couldNotLoad", {
            message: (members.error as Error).message,
          })}
        </p>
      )}
      {members.data && all.length === 0 && (
        <p className="card text-sm text-nb-text-2">
          {t("admin.members.noActiveMembers")}
        </p>
      )}
      {members.data && all.length > 0 && shown.length === 0 && (
        <p className="card text-sm text-nb-text-2">{t("admin.members.noMatch")}</p>
      )}
      {shown.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {shown.map((m) => (
            <MemberCard
              key={m.user_id}
              member={m}
              presence={presenceBy.get(m.user_id)}
              operator={operatorBy.get(m.user_id)}
            />
          ))}
        </div>
      )}

      <DeactivatedMembers />
    </section>
  );
}
